import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient, type QueryKey } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { toastApiError } from "@/lib/errorMessages";
import {
  adminNotificationService,
  type AdminNotification,
  type AdminNotificationListResponse,
} from "@/services/adminNotificationService";
import { adminNotificationKeys } from "@/hooks/useAdminNotificationStatusCounts";
import { notificationStaysInTab, type StatusTab } from "./notificationFilters";

export type NotificationAction = "acknowledge" | "resolve" | "reopen";

const ACTION_REQUESTS: Record<
  NotificationAction,
  (id: string) => Promise<AdminNotification>
> = {
  acknowledge: (id) => adminNotificationService.acknowledge(id),
  resolve: (id) => adminNotificationService.resolve(id),
  reopen: (id) => adminNotificationService.reopen(id),
};

/** Cached list after `updated` changed status: replaced in place, or dropped when it left the tab. */
export function applyNotificationUpdate(
  list: AdminNotificationListResponse,
  updated: AdminNotification,
  staysInTab: boolean,
): AdminNotificationListResponse {
  if (!staysInTab) {
    return {
      total: Math.max(0, list.total - 1),
      items: list.items.filter((row) => row.id !== updated.id),
    };
  }
  return {
    ...list,
    items: list.items.map((row) => (row.id === updated.id ? updated : row)),
  };
}

interface UseNotificationActionOptions {
  /** Cached list the updated notification is patched into. */
  listQueryKey: QueryKey;
  /** Status tab that list shows. */
  tab: StatusTab;
  /** Runs after a successful action, once the cached list is patched. */
  onUpdated?: (id: string, staysInTab: boolean) => void;
}

/** Acknowledge / resolve / reopen one notification, with toasts and cache update. */
export function useNotificationAction({
  listQueryKey,
  tab,
  onUpdated,
}: UseNotificationActionOptions) {
  const { t } = useTranslation("adminNotifications");
  const queryClient = useQueryClient();
  const [actionId, setActionId] = useState<string | null>(null);

  const runAction = async (id: string, action: NotificationAction) => {
    setActionId(id);
    try {
      const updated = await ACTION_REQUESTS[action](id);
      const staysInTab = notificationStaysInTab(tab, updated.status);

      queryClient.setQueryData<AdminNotificationListResponse>(
        listQueryKey,
        (prev) => prev && applyNotificationUpdate(prev, updated, staysInTab),
      );
      void queryClient.invalidateQueries({
        queryKey: adminNotificationKeys.all,
      });
      onUpdated?.(id, staysInTab);

      toast.success(t(`actions.${action}Success`));
    } catch (error) {
      toastApiError(error, undefined, t(`actions.${action}Error`));
    } finally {
      setActionId(null);
    }
  };

  return { actionId, runAction };
}
